import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut, Upload, Trash2, Loader2, Film, ShieldCheck, Plus, Save, X,
  Tags, Inbox, ClipboardList, Check, RotateCcw, ChevronDown, ChevronUp,
  Users, Crown, Ban, RefreshCw, Home, FileEdit,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIERS = [
  { value: "standard", label: "Standard" },
  { value: "vip", label: "VIP" },
];

const GUN_CATEGORIES = [
  { value: "ar", label: "Assault Rifle" },
  { value: "smg", label: "SMG" },
  { value: "lmg", label: "LMG" },
  { value: "shotgun", label: "Shotgun" },
  { value: "sniper", label: "Sniper" },
  { value: "marksman", label: "Marksman" },
  { value: "pistol", label: "Pistol" },
];

const emptyPkg = {
  id: null, name: "", tagline: "", price: "", features: "",
  tier: "standard", sort_order: 99, stripe_link: "",
};

export default function ContentTab() {
  const [packages, setPackages] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/packages`).then((r) => {
      const list = r.data.filter((p) => p.tier !== "vip");
      setPackages(list);
      if (list[0]) setSelectedId(list[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    setLoading(true);
    axios
      .get(`${API}/admin/packages/${selectedId}/content`)
      .then((r) => setContent(r.data.content))
      .catch(() => toast.error("Failed to load content"))
      .finally(() => setLoading(false));
  }, [selectedId]);

  const setGun = (idx, patch) => {
    setContent((c) => {
      const guns = [...(c.guns || [])];
      guns[idx] = { ...guns[idx], ...patch };
      return { ...c, guns };
    });
  };

  const addGun = () => {
    setContent((c) => ({
      ...c,
      guns: [...(c.guns || []), { name: "New gun", category: "ar", enabled: true, value: "" }],
    }));
  };

  const removeGun = (idx) => {
    setContent((c) => ({ ...c, guns: (c.guns || []).filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/packages/${selectedId}/content`, content);
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!packages.length) return <div className="text-white/40 text-sm">Loading…</div>;

  const grouped = (content?.guns || []).reduce((acc, g, idx) => {
    const k = g.category || "ar";
    (acc[k] = acc[k] || []).push({ ...g, _idx: idx });
    return acc;
  }, {});

  return (
    <div data-testid="content-tab">
      <h2 className="font-display font-bold uppercase text-xl">Value page content</h2>
      <p className="text-white/50 text-sm mt-1">
        Edit what each paid user sees on their unlocked card page. Use the package selector below.
      </p>

      <div className="flex flex-wrap gap-2 mt-6">
        {packages.map((p) => (
          <button
            key={p.id}
            onClick={() => setSelectedId(p.id)}
            className={`text-xs uppercase tracking-widest px-3 py-2 rounded border transition ${
              selectedId === p.id
                ? "border-[var(--rv-red)] bg-[var(--rv-red)]/10 text-white"
                : "border-white/15 text-white/70 hover:border-white/30"
            }`}
            data-testid={`content-pkg-${p.id}`}
          >
            {p.name}
          </button>
        ))}
      </div>

      {loading || !content ? (
        <div className="text-white/40 text-sm mt-8">Loading content…</div>
      ) : (
        <div className="mt-8 space-y-6">
          <div className="rv-card rounded-xl p-6 space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-white/60">Intro / setup notes</Label>
              <Textarea
                rows={3}
                value={content.intro || ""}
                onChange={(e) => setContent({ ...content, intro: e.target.value })}
                className="bg-black/40 border-white/10 text-white"
                placeholder="Anything the buyer should read before applying the values…"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-white/60">Video URL (optional)</Label>
              <Input
                value={content.video_url || ""}
                onChange={(e) => setContent({ ...content, video_url: e.target.value })}
                placeholder="https://… (YouTube or direct mp4)"
                className="bg-black/40 border-white/10 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-white/60">Footer notes (optional)</Label>
              <Textarea
                rows={2}
                value={content.notes || ""}
                onChange={(e) => setContent({ ...content, notes: e.target.value })}
                className="bg-black/40 border-white/10 text-white"
              />
            </div>
          </div>

          <div className="rv-card rounded-xl p-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="font-display font-bold uppercase text-lg">Gun cards</h3>
                <p className="text-white/45 text-xs mt-1">ARs show H/V per scope (1× / 2× / 3× / 4×). Others use a single value field.</p>
              </div>
              <button onClick={addGun} className="btn-ghost btn-sm">
                <Plus size={12} /> Add gun
              </button>
            </div>

            <div className="space-y-4 mt-5">
              {Object.entries(grouped).map(([cat, guns]) => (
                <div key={cat}>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/45 mb-2">
                    {GUN_CATEGORIES.find((c) => c.value === cat)?.label || cat}
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    {guns.map((g) => (
                      <div
                        key={g._idx}
                        className={`border rounded-lg p-4 ${g.enabled ? "border-white/10 bg-black/30" : "border-white/5 bg-black/20 opacity-60"}`}
                        data-testid={`gun-row-${g._idx}`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={!!g.enabled}
                            onChange={(e) => setGun(g._idx, { enabled: e.target.checked })}
                            className="accent-[var(--rv-red)] h-4 w-4"
                          />
                          <Input
                            value={g.name}
                            onChange={(e) => setGun(g._idx, { name: e.target.value })}
                            className="bg-transparent border-white/10 text-white font-display uppercase tracking-tight"
                          />
                          <select
                            value={g.category}
                            onChange={(e) => setGun(g._idx, { category: e.target.value })}
                            className="bg-black/40 border border-white/10 text-white rounded h-9 px-2 text-xs"
                          >
                            {GUN_CATEGORIES.map((c) => (
                              <option key={c.value} value={c.value}>{c.label}</option>
                            ))}
                          </select>
                          <button onClick={() => removeGun(g._idx)} className="text-white/45 hover:text-[var(--rv-red)] p-1.5">
                            <Trash2 size={14} />
                          </button>
                        </div>

                        {g.category === "ar" ? (
                          <div className="mt-3 grid grid-cols-4 gap-2">
                            {["1x", "2x", "3x", "4x"].map((s) => (
                              <div key={s} className="space-y-1">
                                <div className="text-[9px] uppercase tracking-widest text-white/40 text-center">{s.toUpperCase()} optic</div>
                                <Input
                                  value={g[`h_${s}`] || ""}
                                  onChange={(e) => setGun(g._idx, { [`h_${s}`]: e.target.value })}
                                  placeholder="H"
                                  className="bg-black/40 border-white/10 text-white text-xs h-8 text-center"
                                />
                                <Input
                                  value={g[`v_${s}`] || ""}
                                  onChange={(e) => setGun(g._idx, { [`v_${s}`]: e.target.value })}
                                  placeholder="V"
                                  className="bg-black/40 border-white/10 text-white text-xs h-8 text-center"
                                />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="mt-3 grid grid-cols-2 gap-3">
                            <div>
                              <div className="text-[9px] uppercase tracking-widest text-white/40 mb-1 text-center">Horizontal</div>
                              <Input
                                value={g.h_value || ""}
                                onChange={(e) => setGun(g._idx, { h_value: e.target.value })}
                                placeholder="H"
                                className="bg-black/40 border-white/10 text-white text-sm text-center"
                              />
                            </div>
                            <div>
                              <div className="text-[9px] uppercase tracking-widest text-white/40 mb-1 text-center">Vertical</div>
                              <Input
                                value={g.v_value || ""}
                                onChange={(e) => setGun(g._idx, { v_value: e.target.value })}
                                placeholder="V"
                                className="bg-black/40 border-white/10 text-white text-sm text-center"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {(!content.guns || content.guns.length === 0) && (
                <div className="text-white/40 text-sm">No guns yet — click &quot;Add gun&quot; to start.</div>
              )}
            </div>
          </div>

          <button onClick={save} disabled={saving} className="btn-primary w-full justify-center">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <><Save size={16} /> Save changes</>}
          </button>
        </div>
      )}
    </div>
  );
}

